import dayjs from 'dayjs';
import i18n from 'i18n';
import type { PeriodFilterOption } from 'types/shared/filters';
import type { SubWorld } from 'types/shared/worlds';
import { World } from 'types/shared/worlds';
import type { AvailableCountries } from 'types/users.types';
import { isExpert } from 'utils/constants';

// filters data mapping --------------------------------------
export const allowedFilters: Record<World | SubWorld, string[]> = {
  expenses: [
    'attachment',
    'payment',
    'isInvoice',
    isExpert ? 'isValidated' : 'reviewed',
    'source',
    'period',
    'categories',
  ],
  revenues: [
    'status',
    'type',
    isExpert ? 'isValidated' : 'reviewed',
    'payment',
    'isInvoice',
    'source',
    'period',
    'categoryIds',
  ],
  bank: [
    'toClassify',
    'amountSign',
    'transactionType',
    'transactionCategory',
    'period',
    'statementPeriod',
  ],
  taxes: ['type', 'status'],
  settings: [],
  repository: [],

  // sub worlds
  clients: [],
  quotes: ['categoryIds'],
};

export const resolveOptionTranslationBasedOnWorld = (
  world: string,
  option: string | Record<string, string | number>,
  country: AvailableCountries,
): string => {
  if (option === 'credit-note')
    return i18n.t(`invoices.filters.${country}.credit_note`);
  const mapper = {
    [World.REVENUES]: () => i18n.t(`invoices.choices.${String(option)}`),
    [World.BANK]: () => i18n.t(`transactions.choices.${String(option)}`),
    [World.TAXES]: () => i18n.t(`taxes.${String(option)}`),
    default: () => i18n.t(`${world}.choices.${String(option)}`),
  };

  return (mapper[world as keyof typeof mapper] || mapper.default)();
};

const commonKeysMapper = { period: () => 'periods' };
export const filetrsKeyMapper: {
  [key in World | SubWorld]?: { [key: string]: () => string };
} = {
  expenses: { ...commonKeysMapper, categories: () => 'categoryIds' },
  revenues: { ...commonKeysMapper, type: () => 'types' },
  bank: {
    ...commonKeysMapper,
    transactionCategory: () => 'transactionCategories',
    transactionType: () => 'transactionTypes',
  },
};

// filters data displaying and transalting ----------------------------
export const radioKeys: Record<World | SubWorld, string[]> = {
  expenses: ['isInvoice', 'reviewed', 'isValidated'],
  revenues: ['isInvoice', 'reviewed', 'isValidated'],
  bank: ['toClassify', 'amountSign', 'deleted'],
  taxes: ['status'],
  settings: [],
  clients: [],
  quotes: [],
  repository: [],
};

export const translationPrefix: Record<World | SubWorld, string> = {
  expenses: 'expenses.filters.',
  revenues: 'invoices.filters.',
  bank: 'transactions.filters.',
  taxes: 'taxes.filters.',
  settings: 'settings.filter.',
  clients: '',
  quotes: 'invoices.filters.',
  repository: 'repository.filters.',
};

export const filtersIcons: Record<string, JSX.Element> = {};

export const isCategoryFilter = (filterKey: string): boolean =>
  ['category', 'categories', 'categoryIds', 'transactionCategory'].includes(
    filterKey,
  );

export const getPeriodAsFilterOptionValue = (
  period: PeriodFilterOption,
): string => {
  const periodChar = period.frequency?.split('')[0];
  if (periodChar === 'y') return String(period.year);
  return `${periodChar}${`_${period.value}`}${`_${period.year}`}`;
};

export const getPeriodLabel = (period: Record<string, string>) => {
  const periodChar = period.frequency.split('')[0];
  let periodUnit = '';
  switch (periodChar) {
    case 'm':
      periodUnit = dayjs()
        .month(Number(period.value) - 1)
        .format('MMMM');
      break;
    case 'q':
      periodUnit = `${i18n.t('quarter_abbr')}${period.value}`;
      break;
  }
  return `${periodUnit} ${period.year}`;
};
